import * as THREE from 'three';
import { syncDebris, type DebrisProp } from './debris';
// Shared energy slider lives in src/services/energy.ts deliberately.
import { clampEnergy } from '../../../services/energy';
import { createDamageLedger, type DamageLedger, type StampResult } from './ledger';
import { BRICK_PROFILE } from './profiles';
import { buildResidue, emptyResidue, type ResidueStamp, type ResidueState } from './residue';
import {
  WEAPON_PRESETS,
  asTriple,
  resolveWeapon,
  type DamageMaterialProfile,
  type DamageWorldOptions,
  type DamageWorldStats,
  type LastStampCall,
  type StampParams,
  type WeaponKind,
} from './types';
import { buildWallGrid } from './wallGrid';
import { createWallView } from './wallView';

export interface DamageWorld {
  readonly ready: boolean;
  stamp(params: StampParams): StampResult;
  reset(): void;
  getStats(): DamageWorldStats;
  attach(scene: THREE.Scene): void;
  detach(): void;
  dispose(): void;
  step(dt: number): void;
  getPickables(): THREE.Object3D[];
  setDebug(on: boolean): void;
  getDebug(): boolean;
  blitLedgerPreview(canvas: HTMLCanvasElement): void;
  lastStamp(): StampResult | null;
  lastStampCall(): LastStampCall | null;
  getLedger(): DamageLedger;
  getResidue(): ResidueState;
  getDebris(): readonly DebrisProp[];
}

export function createDamageWorld(
  _renderer: THREE.WebGLRenderer,
  opts: DamageWorldOptions = {},
): DamageWorld {
  const profile: DamageMaterialProfile = opts.profile ?? BRICK_PROFILE;
  const grid = buildWallGrid(opts.wallSpec);
  const ledger = createDamageLedger(grid);
  const view = createWallView(grid.spec, ledger);

  let attached: THREE.Scene | null = null;
  let frame = 0;
  let lastStepMs = 0;
  let last: StampResult | null = null;
  let lastCall: LastStampCall | null = null;
  let debug = false;
  const ready = true;
  const stamps: ResidueStamp[] = [];
  let residue: ResidueState = emptyResidue(grid.bricks.length);
  let debris: DebrisProp[] = [];

  const refreshVisuals = (): void => {
    residue = buildResidue(ledger, stamps, profile);
    debris = syncDebris(ledger, stamps, debris, profile);
    view.sync(ledger, residue, stamps, debris);
  };

  const stats = (): DamageWorldStats => {
    const s = ledger.getStats();
    return {
      ...s,
      frame,
      brickCount: grid.bricks.length,
      lastStepMs,
      stains: residue.stainCount,
      scorch: residue.scorchCount,
      debris: debris.length,
    };
  };

  const world: DamageWorld = {
    get ready() {
      return ready;
    },
    stamp(params: StampParams): StampResult {
      const energy = clampEnergy(params.energy);
      const result = ledger.stamp(
        {
          ...params,
          energy,
        },
        params.profile ?? profile,
      );
      const pos = asTriple(params.position);
      stamps.push({
        x: pos[0],
        y: pos[1],
        energy,
        blastFraction: params.blastFraction,
        weapon: result.weapon,
        index: stamps.length + 1,
      });
      refreshVisuals();
      last = result;
      lastCall = {
        position: pos,
        direction: asTriple(params.direction),
        energy,
        intensity: params.intensity,
        blastFraction: params.blastFraction,
        surfaceNormal: asTriple(params.surfaceNormal),
        profileId: (params.profile ?? profile).id,
        weapon: result.weapon,
        removedDelta: result.removedDelta,
        newPaths: result.newPaths,
      };
      return result;
    },
    reset() {
      ledger.reset();
      stamps.length = 0;
      residue = emptyResidue(grid.bricks.length);
      debris = [];
      view.sync(ledger, residue, stamps, debris);
      last = null;
      lastCall = null;
    },
    getStats: stats,
    attach(scene: THREE.Scene) {
      if (attached === scene) return;
      if (attached) attached.remove(view.group);
      scene.add(view.group);
      attached = scene;
    },
    detach() {
      if (attached) attached.remove(view.group);
      attached = null;
    },
    dispose() {
      world.detach();
      view.dispose();
    },
    step(_dt: number) {
      const t0 = performance.now();
      frame += 1;
      lastStepMs = performance.now() - t0;
    },
    getPickables() {
      return view.pickables;
    },
    setDebug(on: boolean) {
      debug = on;
      view.setDebug(on);
      view.sync(ledger, residue, stamps, debris);
    },
    getDebug() {
      return debug;
    },
    blitLedgerPreview(canvas: HTMLCanvasElement) {
      view.blitLedger(canvas, ledger);
    },
    lastStamp() {
      return last;
    },
    lastStampCall() {
      return lastCall;
    },
    getLedger() {
      return ledger;
    },
    getResidue() {
      return residue;
    },
    getDebris() {
      return debris;
    },
  };

  return world;
}

export function stampFromWeapon(
  world: DamageWorld,
  position: readonly [number, number, number],
  direction: readonly [number, number, number],
  energy: number,
  weapon: WeaponKind,
  surfaceNormal: readonly [number, number, number] = [0, 0, 1],
): StampResult {
  const preset = WEAPON_PRESETS[weapon] ?? WEAPON_PRESETS.cannon;
  return world.stamp({
    position,
    direction,
    energy,
    intensity: preset.intensity,
    blastFraction: preset.blastFraction,
    surfaceNormal,
  });
}

export function weaponFromParams(intensity: number, blast: number): WeaponKind {
  return resolveWeapon(intensity, blast);
}
